import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  CalendarClock,
  Check,
  TrendingUp,
  Headphones,
  Wallet,
} from 'lucide-react';

const ContractTermsSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
        {/* Background Gradients */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/4" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/2 translate-x-1/4" />
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

        <div className="w-full max-w-5xl z-10 flex flex-col h-full justify-center relative">

            <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold uppercase tracking-wide mb-3 border border-blue-100">
                    <ShieldCheck className="w-3 h-3" />
                    <span>Simple Terms</span>
                </div>
                <h2 className="text-3xl md:text-[40px] font-bold text-slate-900 mb-2 leading-tight">
                    Contract <span className="text-blue-600">terms</span>
                </h2>
                <p className="text-[14.5px] text-slate-600 max-w-2xl mx-auto leading-relaxed">
                    Simple, transparent terms designed to minimize risk and maximize alignment.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">

                {/* Card 1: Onboarding fee */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="bg-white p-7 rounded-2xl shadow-sm border border-slate-200 flex flex-col relative overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all"
                >
                    <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center text-blue-600">
                            <Wallet className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900">Onboarding + consulting package</h3>
                            <p className="text-sm text-slate-400">One-time setup</p>
                        </div>
                    </div>

                    <div className="space-y-3 flex-1">
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Full white glove onboarding & ongoing support included</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Plus <span className="font-semibold text-slate-800">15 hours of consulting</span> from a DSO-experienced consultant</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">You can apply for a waiver — accepted in some cases*</span>
                        </div>
                    </div>

                    <div className="mt-5 bg-blue-50 rounded-xl border border-blue-100 px-5 py-3.5 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">One-time fee</span>
                        <span className="text-xl font-extrabold text-slate-900">$3,000</span>
                    </div>
                </motion.div>

                {/* Card 2: Month-to-month */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white p-7 rounded-2xl shadow-sm border border-slate-200 flex flex-col relative overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all"
                >
                    <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center text-blue-600">
                            <CalendarClock className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900">Month-to-month contract</h3>
                            <p className="text-sm text-slate-400">Chance to opt out</p>
                        </div>
                    </div>

                    <div className="space-y-3 flex-1">
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Month-to-month for your first three months</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Then an annual contract once you've had a chance to opt out</span>
                        </div>
                    </div>

                    <div className="mt-5 bg-blue-50 rounded-xl border border-blue-100 px-5 py-3.5 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">First 3 months</span>
                        <span className="text-xl font-extrabold text-slate-900">Month-to-month</span>
                    </div>
                </motion.div>

                {/* Card 3: ROI Guarantee */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white p-7 rounded-2xl shadow-sm border-2 border-blue-200 flex flex-col relative overflow-hidden hover:shadow-lg transition-all"
                >
                    {/* Featured badge */}
                    <div className="absolute top-4 right-4">
                        <span className="bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg">
                            ROI GUARANTEE
                        </span>
                    </div>

                    <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center text-blue-600">
                            <TrendingUp className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900">ROI guarantee</h3>
                            <p className="text-sm text-slate-400">Money-back promise</p>
                        </div>
                    </div>

                    <div className="space-y-3 flex-1">
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">If we don't drive more ROI than we charge, we refund your payment for that month</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Measured by our automated ROI reporting tool — transparent &amp; verifiable</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Aligned incentives — we only win when you win</span>
                        </div>
                    </div>

                    <div className="mt-5 bg-blue-50 rounded-xl border border-blue-100 px-5 py-3.5 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">Coverage</span>
                        <span className="text-xl font-extrabold text-blue-600">First 3 months*</span>
                    </div>
                </motion.div>

                {/* Card 4: Complimentary support */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white p-7 rounded-2xl shadow-sm border border-slate-200 flex flex-col relative overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all"
                >
                    <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 bg-indigo-50 rounded-xl border border-indigo-100 flex items-center justify-center text-indigo-600">
                            <Headphones className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900">Complimentary support</h3>
                            <p className="text-sm text-slate-400">Always included</p>
                        </div>
                    </div>

                    <div className="space-y-3 flex-1">
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Unlimited support — no per-incident fees</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">Dentrix charges <span className="font-bold text-slate-800">$150+ per incident</span> — we charge nothing</span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">We're here whenever your office needs us</span>
                        </div>
                    </div>

                    <div className="mt-5 bg-indigo-50 rounded-xl border border-indigo-100 px-5 py-3.5 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">Cost</span>
                        <span className="text-xl font-extrabold text-indigo-600">$0</span>
                    </div>
                </motion.div>

            </div>

            <p className="text-[11px] text-slate-400 text-center mt-3 leading-relaxed">
                * Waiver applications are reviewed case-by-case and accepted at our discretion. ROI guarantee applies to the first 3 months of service and is measured against our automated ROI reporting tool.
            </p>

        </div>
    </div>
  );
};

export default ContractTermsSlide;
